import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";
import { RMark } from "./RMark";
import { EASE } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const CHIPS = [
    "What does my car need?",
    "PPF vs Ceramic — which one?",
    "What's the current offer?",
    "Book my free inspection",
];

const getSessionId = () => {
    let sid = localStorage.getItem("rz_chat_sid");
    if (!sid) {
        sid = crypto.randomUUID();
        localStorage.setItem("rz_chat_sid", sid);
    }
    return sid;
};

export const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [msgs, open]);

    const send = async (text) => {
        const message = (text ?? input).trim();
        if (!message || busy) return;
        setInput("");
        setBusy(true);
        setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
        try {
            const res = await fetch(`${API}/api/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: getSessionId(), message }),
            });
            if (!res.ok || !res.body) throw new Error("bad response");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const chunks = buf.split("\n\n");
                buf = chunks.pop();
                for (const chunk of chunks) {
                    const line = chunk.trim();
                    if (!line.startsWith("data:")) continue;
                    const payload = line.slice(5).trim();
                    if (payload === "[DONE]") break;
                    try {
                        const parsed = JSON.parse(payload);
                        if (parsed.delta) {
                            setMsgs((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = {
                                    role: "assistant",
                                    content: copy[copy.length - 1].content + parsed.delta,
                                };
                                return copy;
                            });
                        } else if (parsed.error) {
                            throw new Error("assistant unavailable");
                        }
                    } catch (e) {
                        if (e.message === "assistant unavailable") throw e;
                    }
                }
            }
            setMsgs((m) => {
                const copy = [...m];
                if (!copy[copy.length - 1].content) {
                    copy[copy.length - 1] = {
                        role: "assistant",
                        content:
                            "I'm having trouble connecting right now — tap the WhatsApp button and the team will answer you directly.",
                    };
                }
                return copy;
            });
        } catch {
            setMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    content:
                        "I'm having trouble connecting right now — tap the WhatsApp button and the team will answer you directly.",
                };
                return copy;
            });
        }
        setBusy(false);
    };

    return (
        <>
            <button
                data-testid="chat-launcher"
                onClick={() => setOpen((o) => !o)}
                aria-label="Open Revautoz concierge chat"
                className="fixed bottom-[76px] left-4 z-40 flex items-center justify-center border border-rz-steel bg-rz-black/85 text-rz-white backdrop-blur-md transition-colors duration-300 hover:border-rz-red md:bottom-6 md:left-6"
                style={{ height: 52, width: 52 }}
            >
                {open ? <X size={20} strokeWidth={1.75} /> : <MessageSquare size={20} strokeWidth={1.75} />}
                {!open && (
                    <span className="absolute -right-1 -top-1 h-2.5 w-2.5 bg-rz-red" aria-hidden="true" />
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="chat-panel"
                        role="dialog"
                        aria-label="Revautoz concierge"
                        className="rz-corners fixed bottom-[140px] left-4 right-4 z-[85] flex h-[58vh] flex-col border border-rz-steel bg-rz-black/95 backdrop-blur-md sm:bottom-6 sm:left-6 sm:right-auto sm:h-[540px] sm:w-[400px]"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.4, ease: EASE }}
                    >
                        <div className="flex items-center gap-3 border-b border-rz-steel/60 px-5 py-4">
                            <RMark className="h-5 w-5 shrink-0 text-rz-white" />
                            <div className="min-w-0">
                                <p className="font-display text-[12px] font-bold uppercase tracking-[0.2em] text-rz-white">
                                    Revautoz Concierge
                                </p>
                                <p className="text-[11px] text-rz-smoke">
                                    Ask about services, offers, inspections
                                </p>
                            </div>
                        </div>

                        <div ref={bodyRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
                            {msgs.length === 0 && (
                                <div>
                                    <p className="text-sm leading-relaxed text-rz-smoke">
                                        Tell me about your car — I'll point you to the right
                                        service, or set up your free inspection.
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {CHIPS.map((c) => (
                                            <button
                                                key={c}
                                                data-testid={`chat-chip-${c.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
                                                onClick={() => send(c)}
                                                className="border border-rz-steel px-3 py-2 text-left text-[12px] text-rz-white/85 transition-colors duration-300 hover:border-rz-red hover:text-rz-white"
                                            >
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {msgs.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={i === msgs.length - 1 && m.role === "assistant" ? "chat-reply" : undefined}
                                    className={`max-w-[88%] px-4 py-3 text-[13px] leading-relaxed ${
                                        m.role === "user"
                                            ? "ml-auto bg-rz-red text-white"
                                            : "border border-rz-steel/60 bg-rz-graphite/60 text-rz-white/90"
                                    }`}
                                >
                                    {m.content ||
                                        (busy && m.role === "assistant" ? (
                                            <span className="inline-flex gap-1" aria-label="Typing">
                                                <span className="h-1.5 w-1.5 animate-pulse bg-rz-red" />
                                                <span className="h-1.5 w-1.5 animate-pulse bg-rz-red [animation-delay:150ms]" />
                                                <span className="h-1.5 w-1.5 animate-pulse bg-rz-red [animation-delay:300ms]" />
                                            </span>
                                        ) : null)}
                                </div>
                            ))}
                        </div>

                        <form
                            className="flex gap-2 border-t border-rz-steel/60 p-3"
                            onSubmit={(e) => {
                                e.preventDefault();
                                send();
                            }}
                        >
                            <input
                                data-testid="chat-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask the concierge…"
                                className="min-w-0 flex-1 border border-rz-steel bg-transparent px-3.5 py-3 text-sm text-rz-white outline-none transition-colors placeholder:text-rz-smoke/60 focus:border-rz-red"
                            />
                            <button
                                data-testid="chat-send"
                                type="submit"
                                disabled={busy}
                                aria-label="Send message"
                                className="flex w-12 shrink-0 items-center justify-center bg-rz-red text-white transition-colors duration-300 hover:bg-rz-reddeep disabled:opacity-60"
                            >
                                <Send size={16} />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
